/**
 * Save / Undo decision derived from a {@link ReplaceDeviceOutcome}.
 *
 * Pure + importable without pulling `replaceDevice`'s agent/protobuf graph.
 * Several Jest suites stub `#~/video/components/settings/utils.js` with only
 * `replaceDevice`; `wiring.ts` must not named-import this helper from that
 * mocked module (Jest ESM then fails at link time with "does not provide an
 * export named 'interpretReplaceOutcome'", which is what broke
 * `auto-configured-label-mount.test.tsx` in CI).
 *
 * Per `think-in-systems` (ai.knowledge/global): test the primitive the hook
 * composes (`utils.test.ts`), not the settings wiring hook.
 */

/** Outcome of `replaceDevice`. On success `deviceId` is the RESOLVED live id actually replaced (a promoted placeholder differs from the frozen id), so callers clear the right hold. */
export type ReplaceDeviceOutcome =
  | {
      ok: true;
      deviceId: string;
      verification?: import('./connection-verification.js').ConnectionVerification;
      /** Per-aspect detail behind `verification` (which aspect confirmed or rejected). */
      verificationAspects?: import('./connection-verification.js').AspectOutcomes;
    }
  | { ok: false; reason: 'no-live-device' | 'add-failed' | 'rolled-back' };

/**
 * Did the reload actually bind the device, and — when it did not — the
 * user-facing error to surface. `replaceDevice` turns a failed re-add into a
 * RETURNED `{ok:false}` (it does not throw), so a caller that ignored the
 * return reported success even when the camera was dropped.
 *
 * @param outcome - The result of `replaceDevice`.
 * @param deviceName - The device's name, for the message; falls back to a generic label.
 * @returns `{succeeded:true}` on `ok`, else `{succeeded:false, error}` with a message.
 */
export const interpretReplaceOutcome = (
  outcome: ReplaceDeviceOutcome,
  deviceName?: string
): { succeeded: boolean; deviceId?: string; error?: string } => {
  if (outcome.ok) {
    return { succeeded: true, deviceId: outcome.deviceId };
  }

  const name = deviceName ?? 'the device';

  // `rolled-back`: prior config is live again; new settings did not land.
  // `add-failed`: the camera is gone. `no-live-device`: never found a live id.
  // Never over-claim a restore that did not happen.
  const error =
    outcome.reason === 'rolled-back'
      ? `Could not save changes to ${name}. Previous settings were restored.`
      : outcome.reason === 'add-failed'
        ? `Could not save changes to ${name}. Please try again.`
        : `${name} is no longer available. Re-add it to change its settings.`;

  return { succeeded: false, error };
};
